import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";

const getErrorMsg = (err, fallback = "Erro") => {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e.msg || e.message || JSON.stringify(e)).join("; ");
  return fallback;
};

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit, Trash2, Shield, Crown, Ban, CheckCircle } from "lucide-react";
import api, { handleApiError, formatDateTime } from "@/lib/api";
import { toast } from "sonner";

const PERM_MODULES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "colaboradores", label: "Colaboradores" },
  { key: "usuarios", label: "Usuários" },
  { key: "aprovacoes", label: "Aprovações" },
  { key: "fechamento", label: "Fechamento" },
  { key: "pagamentos", label: "Pagamentos" },
  { key: "alterar_beneficiario", label: "Alterar Beneficiário" },
  { key: "historico", label: "Histórico" },
  { key: "configuracoes", label: "Configurações" },
];

const PERM_LABELS = { full: "Acesso Full", view: "Visualização", none: "Nenhum" };
const PERM_COLORS = {
  full: "bg-emerald-500/15 text-emerald-400",
  view: "bg-blue-500/15 text-blue-400",
  none: "bg-white/5 text-muted-foreground",
};

const emptyForm = { name: "", email: "", password: "", permissions: {}, is_master: false };

export default function CompanyUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [emailCheck, setEmailCheck] = useState({ exists: false, name: "", checked: false });

  const isMaster = user?.is_master;
  const canManage = isMaster || ((user?.permissions || {}).usuarios === "full");

  const fetchUsers = useCallback(async () => {
    try { const res = await api.get("/company-users"); setUsers(res.data); } catch (err) { toast.error(handleApiError(err, "Erro ao carregar usuários")); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => {
    const defaultPerms = {};
    PERM_MODULES.forEach(m => { defaultPerms[m.key] = m.key === "usuarios" || m.key === "configuracoes" ? "none" : "full"; });
    setForm({ ...emptyForm, permissions: defaultPerms });
    setEditingId(null);
    setEmailCheck({ exists: false, name: "", checked: false });
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setForm({
      name: u.name, email: u.email, password: "",
      permissions: u.permissions || {},
    });
    setEditingId(u.id);
    setEmailCheck({ exists: false, name: "", checked: false });
    setDialogOpen(true);
  };

  const checkEmail = async (email) => {
    if (!email || email.length < 5 || editingId) { setEmailCheck({ exists: false, name: "", checked: false }); return; }
    try {
      const res = await api.get(`/company-users/check-email?email=${encodeURIComponent(email)}`);
      setEmailCheck({ ...res.data, checked: true });
      if (res.data.exists && res.data.name) setForm(prev => ({ ...prev, name: res.data.name }));
    } catch { setEmailCheck({ exists: false, name: "", checked: false }); }
  };

  const [masterConfirmDialog, setMasterConfirmDialog] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Nome e email são obrigatórios"); return; }
    if (!editingId && !emailCheck.exists) {
      if (!form.password || form.password.length < 8) { toast.error("Senha mínima de 8 caracteres"); return; }
      if (!/[A-Z]/.test(form.password) || !/[0-9]/.test(form.password) || !/[!@#$%^&*()_+\-=[\]{};:'",.<>?/\\|`~]/.test(form.password)) {
        toast.error("Senha precisa de maiúscula, número e caractere especial"); return;
      }
    }

    // Creating as master: show confirmation dialog first
    if (!editingId && form.is_master && !masterConfirmDialog) {
      setMasterConfirmDialog(true);
      return;
    }
    setMasterConfirmDialog(false);

    // Demote master: redirect to confirmation flow
    if (editingId && form._was_master && !form.is_master) {
      try {
        const res = await api.post(`/company-users/${editingId}/request-demote-master`);
        toast.success(res.data.message);
        setDialogOpen(false);
      } catch (err) { toast.error(getErrorMsg(err, "Erro ao solicitar rebaixamento")); }
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name, email: form.email,
        permissions: form.permissions,
        is_master: form.is_master,
        ...(form.password ? { password: form.password } : {}),
      };
      if (editingId) {
        await api.put(`/company-users/${editingId}`, payload);
        toast.success(`${form.name} atualizado com sucesso`);
      } else {
        const res = await api.post("/company-users", payload);
        if (res.data.linked_existing) {
          toast.success(`${res.data.name} já possuía conta na Kredz e foi vinculado à empresa`);
        } else {
          toast.success(`${form.name} criado com sucesso`);
        }
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err) { toast.error(getErrorMsg(err, "Erro ao salvar")); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/company-users/${deleteDialog}`);
      toast.success("Acesso removido com sucesso");
      setDeleteDialog(null);
      fetchUsers();
    } catch (err) { toast.error(getErrorMsg(err, "Erro ao remover")); }
  };

  const updatePerm = (key, value) => {
    setForm(prev => ({ ...prev, permissions: { ...prev.permissions, [key]: value } }));
  };

  const handleBlock = async (accountId) => {
    try {
      const res = await api.post(`/company-users/${accountId}/block`);
      toast.success(res.data.message);
      fetchUsers();
    } catch (err) { toast.error(getErrorMsg(err, "Erro")); }
  };

  const handleRequestDeleteMaster = async (accountId) => {
    try {
      const res = await api.post(`/company-users/${accountId}/request-delete-master`);
      toast.success(res.data.message);
    } catch (err) { toast.error(getErrorMsg(err, "Erro")); }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="loading-spinner w-8 h-8" /></div>;

  return (
    <div className="p-4 pt-16 lg:pt-4 lg:p-8 space-y-6 lg:space-y-8" data-testid="company-users">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Usuários</h1>
          <p className="text-sm text-muted-foreground mt-2">Gerencie os usuários que acessam a plataforma da empresa</p>
        </div>
        {canManage && (
          <Button data-testid="add-user-btn" onClick={openCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Novo Usuário
          </Button>
        )}
      </div>

      <Card className="border-white/5">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Nome</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Papel</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Permissões</TableHead>
                {canManage && <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Ações</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`} className={`border-white/5 hover:bg-white/[0.02] ${u.blocked ? "opacity-40" : ""}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{u.name}</span>
                      {u.is_master && <Crown className="w-3.5 h-3.5 text-yellow-400" title="Master" />}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    {u.blocked ? (
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-500/15 text-red-400">Bloqueado</span>
                    ) : (
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${u.is_master ? "bg-yellow-500/15 text-yellow-400" : "bg-blue-500/15 text-blue-400"}`}>
                        {u.is_master ? "Master" : "Usuário"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {PERM_MODULES.map(m => {
                        const level = (u.permissions || {})[m.key] || "none";
                        if (level === "none") return null;
                        return (
                          <span key={m.key} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${PERM_COLORS[level]}`}>
                            {m.label.slice(0, 5)}{level === "view" ? " (V)" : ""}
                          </span>
                        );
                      })}
                    </div>
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {!u.is_master && (
                          <Button variant="ghost" size="icon" data-testid={`block-user-btn-${u.account_id}`}
                            onClick={() => handleBlock(u.account_id)}
                            className={`h-8 w-8 ${u.blocked ? "text-emerald-400 hover:text-emerald-300" : "text-muted-foreground hover:text-orange-400"}`}
                            title={u.blocked ? "Desbloquear" : "Bloquear"}>
                            {u.blocked ? <CheckCircle className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                          </Button>
                        )}
                        {(!u.is_master || isMaster) && (
                          <Button variant="ghost" size="icon" data-testid={`edit-user-btn-${u.account_id}`}
                            onClick={() => openEdit(u)} className="h-8 w-8 text-muted-foreground hover:text-foreground">
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {u.is_master && isMaster ? (
                          <Button variant="ghost" size="icon" data-testid={`delete-master-btn-${u.account_id}`}
                            onClick={() => handleRequestDeleteMaster(u.account_id)}
                            className="h-8 w-8 text-muted-foreground hover:text-red-400" title="Solicitar exclusão master">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        ) : !u.is_master ? (
                          <Button variant="ghost" size="icon" data-testid={`delete-user-btn-${u.account_id}`}
                            onClick={() => setDeleteDialog(u.account_id)}
                            className="h-8 w-8 text-muted-foreground hover:text-red-400">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg border-white/10 max-h-[85vh] overflow-y-auto" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar" : "Novo"} Usuário</DialogTitle>
            <DialogDescription>Configure o acesso do usuário à plataforma</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Nome completo <span className="text-red-400">*</span></Label>
                <Input data-testid="user-name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-black/20 border-white/10" />
              </div>
              <div className="space-y-2">
                <Label>Email <span className="text-red-400">*</span></Label>
                <Input data-testid="user-email-input" type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  onBlur={(e) => !editingId && checkEmail(e.target.value)}
                  disabled={!!editingId}
                  className="bg-black/20 border-white/10" />
              </div>
              {!editingId && emailCheck.checked && emailCheck.exists ? (
                <div className="flex items-center p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <p className="text-xs text-blue-400">Este email já possui conta na Kredz (<strong>{emailCheck.name}</strong>). Será vinculado à empresa com as permissões definidas abaixo.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>{editingId ? "Nova senha (opcional)" : "Senha"} {!editingId && <span className="text-red-400">*</span>}</Label>
                  <Input data-testid="user-password-input" type="password" value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder={editingId ? "Deixe vazio para manter" : "Mínimo 8 caracteres"}
                    className="bg-black/20 border-white/10" />
                </div>
              )}
            </div>

            {isMaster && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Crown className="w-4 h-4 text-yellow-400" />
                <Label className="text-sm font-semibold">Tipo de Usuário</Label>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm({ ...form, is_master: true })}
                  className={`flex-1 p-3 rounded-lg border text-sm font-medium text-center transition-colors ${form.is_master ? "border-yellow-500/40 bg-yellow-500/10 text-yellow-400" : "border-white/10 bg-black/10 text-muted-foreground hover:bg-black/20"}`}
                  data-testid="user-type-master">
                  Master
                </button>
                <button type="button" onClick={() => setForm({ ...form, is_master: false })}
                  className={`flex-1 p-3 rounded-lg border text-sm font-medium text-center transition-colors ${!form.is_master ? "border-blue-500/40 bg-blue-500/10 text-blue-400" : "border-white/10 bg-black/10 text-muted-foreground hover:bg-black/20"}`}
                  data-testid="user-type-normal">
                  Normal
                </button>
              </div>
              {editingId && form._was_master && !form.is_master && (
                <div className="mt-2 p-2.5 rounded-lg bg-yellow-500/5 border border-yellow-500/10">
                  <p className="text-xs text-yellow-400 font-medium">Para rebaixar um master, será necessário:</p>
                  <p className="text-[10px] text-muted-foreground mt-1">Confirmação por email do próprio master ou via suporte Kredz comprovando que o mesmo não é sócio da empresa na data de hoje.</p>
                </div>
              )}
              {form.is_master && (
                <p className="text-xs text-muted-foreground mt-2">Destinado aos sócios da empresa. Usuários master têm acesso total e podem gerenciar todos os outros usuários e configurações.</p>
              )}
            </div>
            )}

            <div>
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-blue-400" />
                <Label className="text-sm font-semibold">Permissões</Label>
              </div>
              <div className="space-y-2">
                {PERM_MODULES.map(m => (
                  <div key={m.key} className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5"
                    data-testid={`perm-row-${m.key}`}>
                    <span className="text-sm font-medium">{m.label}</span>
                    <Select value={(form.permissions || {})[m.key] || "none"}
                      onValueChange={(v) => updatePerm(m.key, v)}>
                      <SelectTrigger className="w-[160px] h-8 bg-black/20 border-white/10 text-xs" data-testid={`perm-select-${m.key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full">Acesso Full</SelectItem>
                        <SelectItem value="view">Visualização</SelectItem>
                        <SelectItem value="none">Nenhum</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10">Cancelar</Button>
            <Button data-testid="save-user-btn" onClick={handleSave} disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
              {saving ? <div className="loading-spinner" /> : editingId ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="sm:max-w-sm border-white/10" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle>Confirmar exclusão</DialogTitle>
            <DialogDescription>O usuário perderá acesso à plataforma imediatamente.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} className="border-white/10">Cancelar</Button>
            <Button data-testid="confirm-delete-user-btn" onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Master Creation Confirmation */}
      <Dialog open={masterConfirmDialog} onOpenChange={(v) => { if (!v) setMasterConfirmDialog(false); }}>
        <DialogContent className="sm:max-w-md border-white/10" data-testid="master-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-yellow-400">Criar usuário Master?</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <p className="text-xs text-yellow-400 font-medium">Atenção: acesso Master é recomendado apenas para sócios da empresa.</p>
            </div>
            <div className="text-xs text-muted-foreground space-y-2">
              <p>Usuários master têm <strong className="text-foreground">acesso total</strong> à plataforma, incluindo:</p>
              <p>· Gerenciar todos os colaboradores e usuários</p>
              <p>· Fechar meses e enviar pagamentos</p>
              <p>· Criar e revogar API Keys</p>
              <p>· Configurar fluxos e taxas</p>
            </div>
            <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/10">
              <p className="text-[11px] text-red-400">A remoção de um master requer confirmação por email do próprio master ou via suporte Kredz comprovando que o mesmo não é sócio da empresa.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMasterConfirmDialog(false)} className="border-white/10">Cancelar</Button>
            <Button data-testid="confirm-master-create-btn" onClick={handleSave}
              className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold">Sim, criar como Master</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
